import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "../api";
import { useAuth, type AuthUser } from "../auth";
import { sitePath, siteResolveQuery, useSiteSlug } from "../site-context";

export function SiteRegisterPage() {
  const { slug: paramSlug = "" } = useParams();
  const slug = useSiteSlug(paramSlug);
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [businessName, setBusinessName] = useState("");
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    addressLine1: "",
    city: "",
    region: "",
    postalCode: "",
    country: "US",
    acceptTerms: false,
  });

  useEffect(() => {
    const qs = siteResolveQuery(slug, true);
    if (!qs.includes("slug=") && !qs.includes("host=")) return;
    void apiRequest<{ site: { business: { id: string; name: string } } }>(
      `/sites/resolve?${qs}`,
    )
      .then((data) => {
        setBusinessName(data.site.business.name);
        setBusinessId(data.site.business.id);
      })
      .catch((err: Error) => setError(err.message));
  }, [slug]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!businessId) return;
    if (!form.acceptTerms) {
      setError("Please accept the terms to continue.");
      return;
    }
    setPending(true);
    setError(null);
    try {
      const result = await apiRequest<{ token: string; user: AuthUser }>(
        `/businesses/${businessId}/portal/register`,
        {
          method: "POST",
          body: JSON.stringify({
            ...form,
            phoneNumber: form.phoneNumber || undefined,
            addressLine1: form.addressLine1 || undefined,
            city: form.city || undefined,
            region: form.region || undefined,
            postalCode: form.postalCode || undefined,
            acceptTerms: true as const,
          }),
        },
      );
      await setSession(result.token, result.user);
      navigate(sitePath(slug, "portal"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed");
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="auth-layout">
      <form className="auth-card" onSubmit={onSubmit}>
        <p className="eyebrow">Client registration</p>
        <h1>Join {businessName || "this business"}</h1>
        <div className="form-grid">
          <label>
            Full name
            <input
              required
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            />
          </label>
          <label>
            Email
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </label>
          <label>
            Phone
            <input
              value={form.phoneNumber}
              onChange={(e) =>
                setForm({ ...form, phoneNumber: e.target.value })
              }
            />
          </label>
          <div className="form-grid two">
            <label>
              Password
              <input
                required
                type="password"
                minLength={8}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </label>
            <label>
              Confirm
              <input
                required
                type="password"
                value={form.confirmPassword}
                onChange={(e) =>
                  setForm({ ...form, confirmPassword: e.target.value })
                }
              />
            </label>
          </div>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={form.acceptTerms}
              onChange={(e) =>
                setForm({ ...form, acceptTerms: e.target.checked })
              }
            />
            I accept the terms
          </label>
          <button
            className="btn btn-primary"
            type="submit"
            disabled={pending || !businessId}
          >
            {pending ? "Creating..." : "Create account"}
          </button>
          {error ? <p className="notice error">{error}</p> : null}
          <p className="notice">
            Already registered?{" "}
            <Link to={sitePath(slug, "login")}>
              Log in to {businessName || "this business"}
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
}
